/**
 * Hooks para Mi Cuenta (Sprint 10.1b).
 *
 * Perfiles propios del user logueado (profiles + guru_profiles si aplica).
 * RLS:
 *   - profiles_self_update: el user puede UPDATE su propio profile
 *   - guru_profiles_self_update: el Guru puede UPDATE su guru_profile
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useUser } from '@/lib/auth';
import type { PayoutMethod } from '@/lib/api/admin';

export interface MyProfile {
  id: string;
  full_name: string | null;
  handle: string | null;
  avatar_url: string | null;
  headline: string | null;
  country_code: string | null;
  is_public_profile: boolean;
  /** Opt-in: Gurumba puede destacar sus aportes públicamente (Radar público, redes). */
  allow_public_highlight: boolean;
  onboarding_completed_at: string | null;
}

export interface MyGuruProfile {
  user_id: string;
  /**
   * Formación académica (ej. "MBA · IE Business School"). Opcional.
   * NO es el titular que va junto al nombre en la ficha: ese es
   * `profiles.headline`. La columna vieja `title` duplicaba el headline y
   * no se publicaba en ningún lado; quedó obsoleta el 1 ago 2026.
   */
  academic_title: string | null;
  extended_bio: string | null;
  years_experience: number | null;
  languages: string[];
  session_price_usd_cents: number;
  session_duration_min: number;
  commission_percent: number;
  payout_method: PayoutMethod | null;
  payout_details: Record<string, string> | null;
  calendar_connected: boolean;
  calendar_email: string | null;
  is_public: boolean;
  is_approved: boolean;
  // Booking rules (Sprint 10.2d)
  min_booking_notice_hours: number;
  max_booking_horizon_days: number;
  buffer_between_sessions_min: number;
}

/**
 * El perfil del user logueado.
 *
 * La clave incluye el id: sin eso, este hook (que App.tsx monta en TODAS las
 * páginas, incluida /login, vía useAnalyticsIdentify) cacheaba `null` mientras
 * el visitante era anónimo. Al loguearse, la navegación es client-side: nadie
 * vuelve a pedirlo, y el header seguía mostrando las iniciales del email en vez
 * de la foto hasta que la entrada se ponía vieja o recargabas la página.
 *
 * Tampoco consulta auth por su cuenta: usa el `user` de la sesión, que es la
 * misma fuente que gobierna la clave. Así el dato y la clave no pueden
 * describir a dos personas distintas.
 */
export function useMyProfile() {
  const user = useUser();
  return useQuery({
    queryKey: ['my-profile', user?.id],
    queryFn: async (): Promise<MyProfile | null> => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, handle, avatar_url, headline, country_code, is_public_profile, allow_public_highlight, onboarding_completed_at')
        .eq('id', user.id)
        .maybeSingle();
      if (error) throw error;
      return data as MyProfile | null;
    },
  });
}

// =====================================================================
// Guru setup status (Sprint 10.2c)
// =====================================================================

export interface GuruSetupStatus {
  profile: {
    avatar: boolean;
    /** `guru_profiles.extended_bio` — la que se publica en "Sobre …". */
    bio: boolean;
    headline: boolean;
    languages: boolean;
    specialties: boolean;
    /** Al menos un subtema core. Los secundarios son opcionales. */
    core_topics: boolean;
    industries: boolean;
  };
  operations: {
    availability_set: boolean;
    calendar_connected: boolean;
  };
  payments_legal: {
    payout_method_set: boolean;
    legals_signed_count: number;
    legals_required: number;
    all_legals_signed: boolean;
    tax_doc_status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'expired' | null;
    tax_doc_approved: boolean;
  };
  visibility: {
    is_approved: boolean;
    is_public: boolean;
  };
  summary: {
    completed: number;
    total: number;
    percent: number;
    can_receive_bookings: boolean;
    can_receive_payouts: boolean;
  };
}

export function useGuruSetupStatus(enabled = true) {
  return useQuery({
    queryKey: ['guru-setup-status'],
    queryFn: async (): Promise<GuruSetupStatus | null> => {
      const { data, error } = await supabase.rpc('guru_setup_status');
      if (error) throw new Error(error.message);
      return data as GuruSetupStatus;
    },
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Versión admin: consulta setup status de cualquier guru (no solo el caller).
 * Usado en /admin/mentors para ver checklist antes de aprobar.
 */
export function useAdminGuruSetupStatus(userId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ['admin-guru-setup-status', userId],
    queryFn: async (): Promise<GuruSetupStatus | null> => {
      if (!userId) return null;
      const { data, error } = await supabase.rpc('admin_guru_setup_status', { _user_id: userId });
      if (error) throw new Error(error.message);
      return data as GuruSetupStatus;
    },
    enabled: enabled && !!userId,
    staleTime: 30_000,
  });
}

/**
 * Marca el onboarding del cliente como completado (welcome tour visto).
 * Idempotente: si ya estaba completado, no hace nada destructivo.
 */
export function useMarkOnboardingComplete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data: userRes } = await supabase.auth.getUser();
      if (!userRes.user) throw new Error('Sin sesión');
      const { error } = await supabase
        .from('profiles')
        .update({ onboarding_completed_at: new Date().toISOString() })
        .eq('id', userRes.user.id)
        .is('onboarding_completed_at', null);  // solo si todavía es null
      if (error) throw new Error(error.message);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-profile'] }),
  });
}

export function useUpdateMyProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (changes: Partial<MyProfile>) => {
      const { data: userRes } = await supabase.auth.getUser();
      if (!userRes.user) throw new Error('Sin sesión');
      const { error } = await supabase
        .from('profiles')
        .update(changes)
        .eq('id', userRes.user.id);
      if (error) {
        // El trigger SQL puede tirar 'profanity_in_full_name' o
        // 'profanity_in_handle'. Traducimos a mensaje legible para el user.
        if (error.message.includes('profanity_in_full_name')) {
          throw new Error('Tu nombre contiene palabras no permitidas. Usa otro.');
        }
        if (error.message.includes('profanity_in_handle')) {
          throw new Error('Tu handle contiene palabras no permitidas. Usa otro.');
        }
        throw new Error(error.message);
      }

      // Sync auth.users.user_metadata cuando cambian campos que se leen como
      // fallback en cualquier vista mientras la query de `profiles` carga
      // (ej. el saludo del hero del dashboard). Sin esto, el user_metadata
      // queda con los valores del signup y se ve un flash del nombre viejo
      // por unos ms en cada hard refresh.
      const metaSync: Record<string, unknown> = {};
      if ('full_name' in changes)  metaSync.full_name  = changes.full_name;
      if ('avatar_url' in changes) metaSync.avatar_url = changes.avatar_url;
      if (Object.keys(metaSync).length > 0) {
        const { error: authErr } = await supabase.auth.updateUser({ data: metaSync });
        // Si falla la sync de metadata no rompemos el flujo — la fuente de
        // verdad ya es la tabla `profiles`. Solo dejamos rastro en consola.
        if (authErr) console.warn('No se pudo sincronizar user_metadata:', authErr);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-profile'] }),
  });
}

/** Rango permitido de la tarifa de sesión 1:1 (Marco, 2026-09-15). Mismo CHECK en la base. */
export const SESSION_PRICE_MIN_CENTS = 15000;
export const SESSION_PRICE_MAX_CENTS = 50000;

/**
 * Referencia de mercado para que el guru fije su tarifa: promedio, mínimo y
 * máximo de los gurus publicados. Sale de la misma RPC pública de agregados
 * que usa /invitacion (`get_invitation_stats`, sin PII).
 */
export function useSessionPriceBenchmark() {
  return useQuery({
    queryKey: ['session-price-benchmark'],
    queryFn: async (): Promise<{ avg_cents: number; min_cents: number; max_cents: number; gurus: number } | null> => {
      const { data, error } = await supabase.rpc('get_invitation_stats');
      if (error || !data) return null;
      const d = data as Record<string, number>;
      return {
        avg_cents: Number(d.price_avg_cents ?? 0),
        min_cents: Number(d.price_min_cents ?? 0),
        max_cents: Number(d.price_max_cents ?? 0),
        gurus: Number(d.gurus ?? 0),
      };
    },
    staleTime: 10 * 60_000,
  });
}

/** Devuelve el guru_profile del user logueado (o null si no es Guru). */
export function useMyGuruProfile(enabled = true) {
  const user = useUser();
  return useQuery({
    queryKey: ['my-guru-profile', user?.id],
    queryFn: async (): Promise<MyGuruProfile | null> => {
      const { data: userRes } = await supabase.auth.getUser();
      if (!userRes.user) return null;
      const { data, error } = await supabase
        .from('guru_profiles')
        .select(`
          user_id, academic_title, extended_bio, years_experience, languages,
          session_price_usd_cents, session_duration_min, commission_percent,
          calendar_connected,
          is_public, is_approved,
          min_booking_notice_hours, max_booking_horizon_days, buffer_between_sessions_min
        `)
        .eq('user_id', userRes.user.id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      // Datos privados del guru (método y datos de pago, email del calendario)
      // viven en guru_private: solo los lee el propio guru o el admin (RLS).
      // Si la lectura falla se muestra el error: callarlo pintaría "sin método
      // de pago" y podría llevar al guru a recargar sus datos.
      const { data: priv, error: privErr } = await supabase
        .from('guru_private')
        .select('payout_details, payout_method, calendar_email')
        .eq('user_id', userRes.user.id)
        .maybeSingle();
      if (privErr) throw privErr;
      return {
        ...data,
        payout_details: priv?.payout_details ?? null,
        payout_method: priv?.payout_method ?? null,
        calendar_email: priv?.calendar_email ?? null,
      } as MyGuruProfile;
    },
    enabled,
  });
}

export function useUpdateMyGuruProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (changes: Partial<Pick<MyGuruProfile,
      'academic_title' | 'extended_bio' | 'years_experience' | 'languages' |
      'session_price_usd_cents' |
      'payout_method' | 'payout_details' |
      'min_booking_notice_hours' | 'max_booking_horizon_days' | 'buffer_between_sessions_min'
    >>) => {
      // Tarifa de sesión: la fija el guru, pero nadie rompe el mercado.
      // La base tiene el mismo CHECK; acá el mensaje es legible.
      if (typeof changes.session_price_usd_cents === 'number') {
        const p = changes.session_price_usd_cents;
        if (!Number.isFinite(p) || p < SESSION_PRICE_MIN_CENTS || p > SESSION_PRICE_MAX_CENTS) {
          throw new Error(`La tarifa por sesión va de USD ${SESSION_PRICE_MIN_CENTS / 100} a USD ${SESSION_PRICE_MAX_CENTS / 100}.`);
        }
      }
      // Regla de la casa: nada de links a LinkedIn en la bio. El trigger de
      // la base tambien lo bloquea; aca lo atajamos antes con mensaje amable.
      if (typeof changes.extended_bio === 'string' && /linkedin\.com|lnkd\.in/i.test(changes.extended_bio)) {
        throw new Error('Los enlaces a LinkedIn no están permitidos en la bio. Cuenta tu experiencia directamente en el texto: tu perfil de Gurumba es tu carta de presentación.');
      }
      const { data: userRes } = await supabase.auth.getUser();
      if (!userRes.user) throw new Error('Sin sesión');
      const uid = userRes.user.id;
      // payout_details y payout_method viven en guru_private; el resto en guru_profiles.
      const { payout_details, payout_method, ...rest } = changes;
      if (Object.keys(rest).length > 0) {
        const { error } = await supabase.from('guru_profiles').update(rest).eq('user_id', uid);
        if (error) {
          if (error.message?.includes('row-level security')) {
            throw new Error('Solo los Gurus pueden editar este perfil.');
          }
          throw new Error(error.message);
        }
      }
      if (payout_details !== undefined || payout_method !== undefined) {
        // Solo se mandan las columnas que cambian (el upsert actualiza lo enviado).
        const privChanges: Record<string, unknown> = { user_id: uid, updated_at: new Date().toISOString() };
        if (payout_details !== undefined) privChanges.payout_details = payout_details;
        if (payout_method !== undefined) privChanges.payout_method = payout_method;
        const { error: pErr } = await supabase.from('guru_private').upsert(privChanges);
        if (pErr) throw new Error(pErr.message);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-guru-profile'] });
      // El estado del registro cambia con esto, y de él viven la barra guía
      // del shell y las tarjetas del hub. Sin invalidar acá, el guru guarda,
      // ve el toast de "guardado" y arriba le sigue diciendo que le falta
      // justo lo que acaba de completar.
      queryClient.invalidateQueries({ queryKey: ['guru-setup-status'] });
    },
  });
}

// =====================================================================
// Preferencias de notificación (in-app)
// =====================================================================
export interface MyNotificationPrefs {
  inapp_notif_community: boolean;
  inapp_notif_bookings: boolean;
  inapp_notif_payments: boolean;
  inapp_notif_reviews: boolean;
  // Correo (opcionales / no transaccionales)
  email_notif_radar_digest: boolean;
  /** Novedades, ofertas y la secuencia de seguimiento (promocionales). */
  email_notif_marketing: boolean;
}

const DEFAULT_PREFS: MyNotificationPrefs = {
  inapp_notif_community: true,
  inapp_notif_bookings: true,
  inapp_notif_payments: true,
  inapp_notif_reviews: true,
  email_notif_radar_digest: true,
  email_notif_marketing: true,
};

export function useMyNotificationPrefs() {
  const user = useUser();
  return useQuery({
    queryKey: ['my-notification-prefs', user?.id],
    queryFn: async (): Promise<MyNotificationPrefs> => {
      const { data: userRes } = await supabase.auth.getUser();
      if (!userRes.user) throw new Error('Sin sesión');
      const { data, error } = await supabase
        .from('user_preferences')
        .select('inapp_notif_community, inapp_notif_bookings, inapp_notif_payments, inapp_notif_reviews, email_notif_radar_digest, email_notif_marketing')
        .eq('user_id', userRes.user.id)
        .maybeSingle();
      if (error) throw new Error(error.message);
      // Si aún no existe la fila (raro: se crea al registrarse), usamos defaults.
      return { ...DEFAULT_PREFS, ...(data ?? {}) } as MyNotificationPrefs;
    },
  });
}

export function useUpdateMyNotificationPrefs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (changes: Partial<MyNotificationPrefs>) => {
      const { data: userRes } = await supabase.auth.getUser();
      if (!userRes.user) throw new Error('Sin sesión');
      // upsert: si la fila no existía, la crea con user_id (RLS self-insert).
      const { error } = await supabase
        .from('user_preferences')
        .upsert({ user_id: userRes.user.id, ...changes }, { onConflict: 'user_id' });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-notification-prefs'] }),
  });
}
